import _ from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import submenuModels from './menus/submenu-models';
import CRUDPage from './CRUDPage';
import NiceDate from '../components/common/NiceDate';
import MongoDocEditor from '../components/lego/MongoDocEditor';
import ModelContextEditor from '../components/lego/ModelContextEditor';
import TrimSpec from '../components/lego/trims/TrimSpec';
import NotesEditor from '../components/common/editors/NotesEditor';
import { LocalesList } from '../components/lego/LocalesList';
import { LocalesSelector } from '../components/lego/LocalesSelector';
import LegoLabelsSelector from '../components/lego/LegoLabelsSelector';
import { Icon } from '../components/common/Icon';
import { IconButton } from '../components/common/IconButton';
import { ModelTypeByCountryEditor } from '../components/lego/ModelTypeByCountryEditor';
import { LocaleFlag } from '../components/lego/LocaleFlag';
import ObjectSearchHandler from '../components/common/ObjectSearchHandler';
import StableUrlsWidget from '../components/lego/stable-urls/StableUrlsWidget';
import { LabelsSummary } from '../components/data-issues/LabelsSummary';
import { LegoFiltersBar } from '../components/common/LegoFiltersBar';
import AskAIAboutModelsModal from '../components/ai/AskAIAboutModelsModal';
import ModalBatchEditDoc, { defaultColumns } from '../components/lego/ModalBatchEditDoc';
import MakeImg from '../components/common/MakeImg';
import BadgePowertrain from '../components/lego/BadgePowertrain';
import { LegoStateBadge } from '../components/lego/LegoStateBadge';
import { MODEL_TYPE_COLORS as typeColor } from '../components/lego/trims/TrimColors';

// Undated models count as the oldest: an Invalid Date compares as equal to everything and breaks the sort
const lastUpdatedSorter = o => new Date(o.updatedAt || o.createdAt || 0);

export default class ModelsManager extends CRUDPage {
  constructor(props) {
    super(props, '/services/data/models/all');

    this.submenu = submenuModels;
    // Most recently updated first (column 0, "Last updated")
    this.state.columnSort = { index: 0, asc: false, sorter: lastUpdatedSorter };

    this.newObjectTemplate = {
      idModelo: '',
      modelo: '',
      marca: '',
      slug: '',
      state: 'published',
      locales: [window.config.locale],
      typeByCountry: { '*': { type: 'model' } },
    };
  }

  getFacetFiltersComponent() {
    const refreshModeloCaches = () => this.runAsync(this.service('/services/data/models').update({}, {}));

    const onModelFilterChange = (newContext) => {
      let newFilters = _.cloneDeep(this.state.facetFilters || {});
      if (_.isEmpty(newContext)) {
        delete newFilters.idModelo;
      } else if (newContext.modelId) {
        newFilters.idModelo = newContext.modelId;
      } else if (newContext.make) {
        newFilters.idModelo = { $regex: '^' + newContext.make };
      }
      this.onFiltersChange(newFilters);
    };

    let { idModelo } = this.state.facetFilters || {};
    let modelContext = null;
    if (idModelo?.$regex) {
      modelContext = { make: idModelo.$regex.slice(1) };
    } else if (idModelo) {
      modelContext = { modelId: idModelo };
    }

    return <span className='d-flex align-items-center'>
      <span style={{ width: '350px', display: 'inline-block' }} className={'zoom-75 ml-2 mr-2'}>
        <ModelContextEditor value={modelContext}
                            onChange={onModelFilterChange}
                            groupsToUse={['makes', 'models', 'families', 'submodels']}/>
      </span>

      <span className={'ml-3 mr-2 btn btn-sm btn-warning'} onClick={refreshModeloCaches}>Refresh site generations cache</span>

      <LegoFiltersBar fields={['state', 'labels', 'locales']} onChange={this.onFiltersChange.bind(this)} filter={this.state.facetFilters} />

      <StableUrlsWidget/>

      <IconButton icon={'assistant'} fill={true} level={'violeta'} small className='ml-2' onClick={this.openAiModelsChat.bind(this)}>AI Models...</IconButton>
    </span>;
  }

  openAiModelsChat() {
    const models = this.state.objects || [];
    this.openModal(
      <AskAIAboutModelsModal models={models} />,
      { title: 'Ask AI about Models', minSize: false }
    );
  }

  buildObjectQuery() {
    const query = { ...super.buildObjectQuery(), $limit: 10000 };
    // Models without state are published (most of them: the field came later), so "published" also matches a missing state
    const states = _.castArray(this.state.facetFilters?.state || []);
    if (states.includes('published')) {
      query.state = { $in: [...states, null] };
    }
    return query;
  }

  getSearchHandler() {
    const dataIssueSearchFilters = {
      family: {
        filter: labelFilter => m => m.type === 'family',
        description: 'Show model family only'
      },
      m: {
        filter: txt => (m => m.modelo && m.modelo.includes(txt)),
        description: 'Search only in model name [=<substring>...]'
      },
      deleted: {
        filter: () => m => m.toDelete,
        description: 'Show deleted models only'
      },
      unpublished: {
        filter: () => m => m.state && m.state !== 'published',
        description: 'Show models not on the site yet (inprogress / unpublished)'
      },
      localeNot: {
        filter: locale => m => !m.locales?.includes(locale),
        description: 'Show models not in locale [=<locale>...]'
      }
    };

    const nodeTextSearchCustomizer = text => text.replace(/\s+/g, '[\\w_-\\s]*');

    return new ObjectSearchHandler(dataIssueSearchFilters, nodeTextSearchCustomizer);
  }

  openBatchEdit(docs) {
    const svc = this.service('/services/data/models/all');
    const columns = [defaultColumns.legoState, defaultColumns.locales];
    this.openModal(
      <ModalBatchEditDoc docs={docs} onCancel={() => this.closeModal()} onSave={() => true} service={svc} columns={columns}/>,
      { title: 'Edit models state and locales in batch' }
    );
  }

  getSelectionButtons(selection) {
    if (selection?.length) {
      return <>
        {super.getSelectionButtons(selection)}
        <IconButton icon={'app_registration'} title={'Edit state and locales in batch'} onClick={() => this.openBatchEdit(selection)}/>
      </>;
    }
    return super.getSelectionButtons(selection);
  }

  getBatchAIEditLabelFields() {
    return ['idModelo'];
  }

  getColumnsDefinition(objects) {
    return [
      { content: 'Last updated', className: 'text-center', sorter: lastUpdatedSorter },
      { content: 'Locales', className: 'text-center', sorter: [o => o.locales?.length, o => o.locales?.sort().join(',') ] },
      { content: 'Type', className: 'text-center' },
      { content: 'Model ID', className: 'text-center', sorter: ['idModelo'] },
      { content: 'Marca', className: 'text-left', sorter: ['marca', 'modelo'] },
      { content: 'Modelo', className: 'text-center', sorter: ['modelo'] },
      { content: 'Slug', className: 'text-center' },
      { content: 'Rebranded From', className: 'text-center' },
      { content: 'Rebranded To', className: 'text-center' },
      { content: 'Redirects, renames, etc', className: 'text-center' },
    ];
  }

  getObjectColumns(modelObject, def, i, allRows) {
    let { countries, typeByCountry, redirectsByCountry, renameByLocale, idModelo, slug, modelo, marca, createdAt, updatedAt, locales, toDelete, labels, state } = modelObject;

    const globalType = typeByCountry?.['*'] || {};

    let redirectsSummary = '';
    if(!_.isEmpty(redirectsByCountry)) {
      redirectsSummary = _.map(redirectsByCountry, (redirects, country) => `${country}: ${redirects[0].oldModelId}`).join(', ');
    }

    let rowClass = toDelete ? 'bg-light-danger' : '';
    return [
      <td key={'id'} className={rowClass}>
        {toDelete ? <span className={'text-danger small'}> <Icon icon={'delete'} small level={'danger'} />Deleted<br/> </span> : null}
        {/* Only published models (or without state) are on the site */}
        {state && state !== 'published' ? <><LegoStateBadge state={state} /><br/></> : null}
        {(updatedAt || createdAt) && <NiceDate t={updatedAt || createdAt} />}
        {labels?.length ? <><br/><LabelsSummary labels={labels} /></> : null}
      </td>,

      <td key={'locales'} className={'text-center ' + rowClass}>
        {locales ? <LocalesList locales={locales} /> : '-'}
      </td>,

      <td key={'type'} className={`text-center bg-light-${typeColor[globalType.type || '-']} ${rowClass}`}>
        <span className={`badge badge-${typeColor[globalType.type || '-']}`}>{globalType.type || '-'}</span>

        {globalType.type === 'submodel' && globalType.parent ? <div className={'text-muted small'}> of <TrimSpec spec={'modelId'} value={globalType.parent} /></div> : null}

        {globalType.type === 'family' ? <div className={'text-muted small'} style={{maxWidth: '300px'}}> with: {(globalType.members || []).map(m => <span key={m} className='mr-1'>
          <TrimSpec spec={'modelId'} value={m.replace(marca+'-', '')} />
          </span>)}</div> : null}
      </td>,

      <td key={'idModelo'} className={'text-center no-wrap ' + rowClass}>
        <TrimSpec spec={globalType.type || 'modelId'} value={idModelo} />
        {globalType.powertrain && globalType.powertrain !== 'ICE' ? <BadgePowertrain powertrain={globalType.powertrain} className='ml-1' /> : null}
      </td>,

      <td key={'marca'} className={'text-left ' + rowClass}>
        <MakeImg make={marca} /> {marca}
      </td>,

      <td key={'context'} className={'text-center ' + rowClass}>{modelo}</td>,

      <td key={'slug'} className={'text-center monospace ' + rowClass}>
        {slug}
      </td>,

      <td key={'renamedFrom'} className={'text-center ' + rowClass}>
        {globalType.rename?.renamedFrom ? <TrimSpec spec={'modelId'} value={globalType.rename.renamedFrom} /> : ''}
        {globalType.rename?.fromYear ? <div className='text-info text-center small'> ⟶ in {globalType.rename.fromYear}</div> : null}
      </td>,

      <td key={'renamedTo'} className={'text-center ' + rowClass}>
        {globalType.rename?.renamedTo ? <TrimSpec spec={'modelId'} value={globalType.rename.renamedTo} /> : ''}
        {globalType.rename?.toYear ? <div className='text-info text-center small'> in {globalType.rename.toYear} ⟶</div> : null}
      </td>,

      <td key={'countries'} className={'text-center small monospace ' + rowClass}>
        { globalType.relatedModels?.length ? <div>Related: {globalType.relatedModels.map(m => <TrimSpec className='mr-1' spec={'modelId'} value={m} key={m} />)}</div> : null}
        {redirectsSummary ?  <Icon icon={'merge_type'} level={'primary'} title={'Redirects from: '+redirectsSummary}/> : null}
        {_.map(renameByLocale, (rename, locale) => <span className='ml-2' key={locale}><LocaleFlag locale={locale} />{rename.modelo}</span>)}
      </td>
    ];
  }

  getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    let schema = {
      locales: (value, onChange) => <LocalesSelector onChange={onChange} value={value} />,
      labels: (value, onChange) => <LegoLabelsSelector onChange={onChange} value={value || []} />,
      idModelo: true,
      marca: true,
      modelo: true,
      slug: true,
      "typeByCountry": (value, onChange) => <ModelTypeByCountryEditor value={value} onChange={onChange} />,
      notes: (value, onChange) => <NotesEditor value={value} onChange={onChange} />,
    };

    let onSave = (changedObj, ...args) => {
      return defaultOnSaveCbk(changedObj, ...args);
    };

    return <MongoDocEditor extendSchema={schema} obj={obj} showFields={_.keys(schema).concat(['renameByLocale'])} onSave={onSave}
      onCancel={defaultOnCancelCbk} />;
  }
}
